import React from 'react';
import { format, startOfDay, isBefore } from 'date-fns';
import { ReactComponent as PrevIcon } from 'assets/icons/navigation/small/chevron-left-sm.svg';
import { ReactComponent as NextIcon } from 'assets/icons/navigation/small/chevron-right-sm.svg';
import {
  MobileWeekCalendarWrapper,
  MobileWeekCalendarNav,
  MobileWeekCalendarNavButton,
  MobileWeekCalendarMonthYear,
  MobileWeekCalendarDaysRow,
  MobileWeekCalendarDayLabel,
  MobileWeekCalendarDayCell,
  MobileWeekCalendarTodayRingWrapper,
  MobileWeekCalendarDayNumberRing,
  MobileWeekCalendarDot,
} from '../styled';

const TodayRingSvg = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    aria-hidden="true"
  >
    <circle cx="16" cy="16" r="15" fill="white" />
    <path d="M 8.5 29 A 15 15 0 1 1 23.5 29" stroke="#F38A00" strokeWidth="1" fill="none" strokeLinecap="round" />
  </svg>
);
import { formatWeekRange } from '../../../utils/dateFormatters';
import type { EventsByDate } from '../../../utils/eventUtils';

const WEEKDAY_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export type DotType = 'red' | 'blue' | 'none';

interface MobileWeekCalendarProps {
  currentDate: Date;
  weekDays: Date[];
  selectedDate: Date;
  onSelectDay: (date: Date) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
  eventsByDate: EventsByDate;
}

export const MobileWeekCalendar: React.FC<MobileWeekCalendarProps> = ({
  currentDate,
  weekDays,
  selectedDate,
  onSelectDay,
  onNavigate,
  eventsByDate,
}) => {
  const today = new Date();
  const todayKey = format(today, 'yyyy-MM-dd');
  const selectedKey = format(selectedDate, 'yyyy-MM-dd');
  const todayStart = startOfDay(today);

  const getDotType = (day: Date, dateKey: string, hasEvents: boolean, isSelected: boolean): DotType => {
    if (isSelected || !hasEvents) return 'none';
    const isPast = isBefore(day, todayStart);
    const isToday = dateKey === todayKey;
    if (isToday || isPast) return 'red';
    return 'blue';
  };

  return (
    <MobileWeekCalendarWrapper>
      <MobileWeekCalendarNav>
        <MobileWeekCalendarNavButton type="button" onClick={() => onNavigate('prev')} aria-label="Semana anterior">
          <PrevIcon />
        </MobileWeekCalendarNavButton>
        <MobileWeekCalendarMonthYear>{formatWeekRange(currentDate)}</MobileWeekCalendarMonthYear>
        <MobileWeekCalendarNavButton type="button" onClick={() => onNavigate('next')} aria-label="Próxima semana">
          <NextIcon />
        </MobileWeekCalendarNavButton>
      </MobileWeekCalendarNav>
      <MobileWeekCalendarDaysRow>
        {WEEKDAY_LABELS.map(label => (
          <MobileWeekCalendarDayLabel key={label}>{label}</MobileWeekCalendarDayLabel>
        ))}
      </MobileWeekCalendarDaysRow>
      <MobileWeekCalendarDaysRow>
        {weekDays.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const isToday = dateKey === todayKey;
          const isSelected = dateKey === selectedKey;
          const hasEvents = (eventsByDate[dateKey]?.length ?? 0) > 0;
          const dotType = getDotType(day, dateKey, hasEvents, isSelected);
          return (
            <MobileWeekCalendarDayCell
              key={dateKey}
              type="button"
              onClick={() => onSelectDay(day)}
              aria-label={format(day, "d 'de' MMMM")}
              aria-pressed={isSelected}
            >
              {isToday && !isSelected ? (
                <MobileWeekCalendarTodayRingWrapper>
                  <TodayRingSvg />
                  <span style={{ position: 'relative', zIndex: 1 }}>{format(day, 'd')}</span>
                </MobileWeekCalendarTodayRingWrapper>
              ) : (
                <MobileWeekCalendarDayNumberRing $isSelected={isSelected}>
                  {format(day, 'd')}
                </MobileWeekCalendarDayNumberRing>
              )}
              <MobileWeekCalendarDot $dotType={dotType} />
            </MobileWeekCalendarDayCell>
          );
        })}
      </MobileWeekCalendarDaysRow>
    </MobileWeekCalendarWrapper>
  );
};
